/**
 * zg-seo-landing.js — behaviour for the SEO landing family.
 *
 * Loaded by `sections/zg-seo-header.liquid` and `sections/zg-seo-audience.liquid`,
 * which only ever render on the `templates/page.zg-seo-*.json` family.
 *
 * SOURCE
 * The 49-line inline <script> at the foot of each of the five PHP templates,
 * byte-identical across all five. It did three things:
 *
 *   1. Mobile nav open/close (hamburger, close button, backdrop, Escape).
 *   2. `.main-nav a` click -> move an `active` class onto the clicked link.
 *   3. `.audience-tab` click -> swap which `.audience-panel` is shown, by
 *      sniffing the tab's TEXT for the words "Young Adults", "Kids" or
 *      "Arthritis" and mapping to a hardcoded `data-tab` value. That is not
 *      portable to editable copy — rename a tab in the theme editor and the
 *      panel stops switching. Here the tab carries the panel's id explicitly,
 *      which is the same behaviour without the text dependency.
 *   4. A `faq-open` class mirrored onto each `<details>` on toggle. No CSS in
 *      `small-guitar.css` or in any page's inline block matches `.faq-open`,
 *      so it was dead. Not ported; `<details open>` and `[open]` in CSS cover
 *      what the page actually styles.
 *
 * Written as two custom elements, per Horizon's convention and the theme
 * CLAUDE.md rule. No jQuery, no framework.
 */

/**
 * Mobile navigation drawer for the SEO landing header.
 *
 * Live toggled `menu-open` on <body>; renamed to `zg-seo-menu-open` so it
 * cannot collide with Horizon's own body classes.
 */
class ZgSeoNav extends HTMLElement {
  connectedCallback() {
    this.toggleButton = this.querySelector('[data-zg-seo-nav-open]');
    this.closeButton = this.querySelector('[data-zg-seo-nav-close]');
    this.backdrop = this.querySelector('[data-zg-seo-nav-backdrop]');
    this.drawer = this.querySelector('[data-zg-seo-nav-drawer]');

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.toggleButton?.addEventListener('click', this.open);
    this.closeButton?.addEventListener('click', this.close);
    this.backdrop?.addEventListener('click', this.close);
    document.addEventListener('keydown', this.onKeydown);

    // Every drawer link is an in-page anchor; leaving the drawer open over the
    // target is a trap on a phone. Live never closed it — deliberate fix.
    this.drawer?.addEventListener('click', (event) => {
      if (event.target.closest('a')) this.close();
    });

    // Live moves an `active` class onto whichever desktop nav link was last
    // clicked (every one is an in-page anchor). Kept for parity.
    this.desktopLinks = Array.from(this.querySelectorAll('[data-zg-seo-nav-link]'));
    this.desktopLinks.forEach((link) => {
      link.addEventListener('click', () => {
        this.desktopLinks.forEach((other) => other.classList.toggle('active', other === link));
      });
    });

    this.setExpanded(false);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.body.classList.remove('zg-seo-menu-open');
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  open() {
    document.body.classList.add('zg-seo-menu-open');
    this.setExpanded(true);
    this.closeButton?.focus();
  }

  close() {
    if (!document.body.classList.contains('zg-seo-menu-open')) return;
    document.body.classList.remove('zg-seo-menu-open');
    this.setExpanded(false);
    this.toggleButton?.focus();
  }

  setExpanded(isOpen) {
    this.toggleButton?.setAttribute('aria-expanded', String(isOpen));
    this.drawer?.setAttribute('aria-hidden', String(!isOpen));
    // Keep the closed drawer out of the tab order. Live left it reachable,
    // which put five invisible links ahead of the page for keyboard users.
    this.drawer?.toggleAttribute('inert', !isOpen);
  }
}

/**
 * The audience tab switcher.
 *
 * Only pages with more than one audience panel emit the tab list at all; with
 * a single panel (as on adult-beginner-guitar) the section renders the panel
 * and no tabs, and this element does nothing.
 *
 * Implemented as a real ARIA tablist: roles, `aria-selected`, and arrow-key
 * roving focus. Live had none of that — the tabs were bare <button>s.
 */
class ZgSeoTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-zg-seo-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-zg-seo-panel]'));

    if (this.tabs.length < 2) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab.dataset.zgSeoTab));
      tab.addEventListener('keydown', (event) => this.onKeydown(event));
    });
  }

  onKeydown(event) {
    const index = this.tabs.indexOf(event.currentTarget);
    let next = null;

    if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.tabs.length - 1;
    if (next === null) return;

    event.preventDefault();
    this.tabs[next].focus();
    this.select(this.tabs[next].dataset.zgSeoTab);
  }

  select(id) {
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.zgSeoTab === id;
      tab.classList.toggle('audience-tab-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.zgSeoPanel === id;
      panel.classList.toggle('audience-panel-active', isActive);
      panel.toggleAttribute('hidden', !isActive);
    });
  }
}

if (!customElements.get('zg-seo-nav')) customElements.define('zg-seo-nav', ZgSeoNav);
if (!customElements.get('zg-seo-tabs')) customElements.define('zg-seo-tabs', ZgSeoTabs);
